import { fromJS, Map } from 'immutable';

export const getIsOpen = (state, routeId, stopId) => state.routeSurveyModal.getIn([routeId, stopId, 'isOpen'], false);

export const getIsLoading = (state, routeId, stopId) => state.routeSurveyModal.getIn([routeId, stopId, 'isLoading'], false);

export const getSelectedIndex = (state, routeId, stopId) => state.routeSurveyModal.getIn([routeId, stopId, 'selectedIndex'], 0);

export const getToasts = (state, routeId, stopId) => state.routeSurveyModal.getIn([routeId, stopId, 'toasts'], Map({}));

const getSurveyQuestions = (surveyQuestions, surveyApplied) => (
  surveyQuestions
    .map((surveyQuestion) => {
      let newSurveyQuestion = surveyQuestion;
      if (newSurveyQuestion.has('subQuestions')) {
        newSurveyQuestion = newSurveyQuestion.set('subQuestions', getSurveyQuestions(newSurveyQuestion.get('subQuestions'), surveyApplied));
      }
      const answers = surveyApplied.get('surveyAnswers').filter((surveyAnswer) => (
        surveyAnswer.getIn(['question', 'id']) === newSurveyQuestion.get('id')
      )).map((surveyAnswer) => surveyAnswer.get('valueAnswered'));
      return newSurveyQuestion.merge({ answers, uniqueId: `${surveyApplied.get('id')}${surveyQuestion.get('id')}` });
    })
    .sortBy((surveyQuestion) => surveyQuestion.get('questionOrder'))
);

export const getSurveyModalData = (state, routeId, stopId) => { 
  const surveysApplied = state.routeSurveyModal.getIn([routeId, stopId, 'surveysApplied'], fromJS([]));
  const surveys = state.routeSurveyModal.getIn([routeId, stopId, 'surveys'], fromJS([]));
  if (!surveysApplied?.size || !surveys?.size) {
    return fromJS([]);
  }

  const getSurveys = (filteredSurveysApplied) => {
    const checkSurveyApplyRulesIds = (surveyApplied) => (surveyApplyRule) => (
      surveyApplyRule.get('id') === surveyApplied.getIn(['surveyApplyRule', 'id'])
    );

    return filteredSurveysApplied.reduce((surveysAppliedWithSurveyData, surveyApplied) => {
      const surveyFound = surveys.find((survey) => (
        survey.get('surveyApplyRules').find(checkSurveyApplyRulesIds(surveyApplied))
      ));

      if (surveyFound) {
        surveysAppliedWithSurveyData.push(surveyApplied.merge(Map({
          title: surveyFound.get('title'),
          surveySections: surveyFound.get('surveySections')
            .sortBy((section) => section.get('sectionOrder'))
            .map((section) => section.set('surveyQuestions', getSurveyQuestions(section.get('surveyQuestions'), surveyApplied)))
        })));
      }

      return surveysAppliedWithSurveyData;
    }, []);
  };

  const route = surveysApplied.find((surveyApplied) => surveyApplied.has('route'))?.get('route');
  const routeSurveys = {
    routeId: route.get('id'),
    surveys: getSurveys(surveysApplied.filter((surveyApplied) => surveyApplied.has('route') && !surveyApplied.has('stop')))
  };

  const uniqueStopsWithAnswers = surveysApplied.reduce((result, curr) => (
    curr.has('stop') && !result.some((res) => res.get('id') === curr.getIn(['stop', 'id'])) ? result.push(curr.get('stop')) : result
  ), fromJS([]));
  const stopsSurveys = uniqueStopsWithAnswers
    .sortBy((stop) => stop.get('actualSequenceNum'))
    .map((stop) => ({
      stopId: stop.get('id'),
      location: stop.get('location'),
      actualSequenceNum: stop.get('actualSequenceNum'),
      surveys: getSurveys(surveysApplied.filter((surveyApplied) => surveyApplied.getIn(['stop', 'id']) === stop.get('id')))
    }));

  return fromJS([routeSurveys, ...stopsSurveys]);
};

export const getSurveyList = (state, routeId, stopId) => (
  getSurveyModalData(state, routeId, stopId).getIn([getSelectedIndex(state, routeId, stopId), 'surveys'], fromJS([]))
    .sortBy((survey) => survey.get('when'))
);

export const getPhotoAlbumFilter = (state, routeId, stopId) => {
  const currentSurveyApplied = getSurveyModalData(state, routeId, stopId).get(getSelectedIndex(state, routeId, stopId));

  const filter = {
    surveyQuestions: [...currentSurveyApplied.get('surveys').reduce((acc, survey) => {
      survey.get('surveyAnswers').forEach((surveyAnswer) => {
        acc.add(surveyAnswer.getIn(['question', 'id']));
      });
      return acc;
    }, new Set())].map((questionId) => ({ id: questionId }))
  };

  if (currentSurveyApplied.has('stopId')) {
    filter.stops = [{ id: currentSurveyApplied.get('stopId') }];
  } else {
    filter.routes = [{ id: currentSurveyApplied.get('routeId') }];
    filter.stops = [null];
  }

  return filter;
};

export const getIsLoadingPhotoAlbums = (state, routeId, stopId) => state.routeSurveyModal.getIn([routeId, stopId, 'photoAlbums', `${getSelectedIndex(state, routeId, stopId)}`, 'isLoading'], false);

export const getPhotoAlbums = (state, { survey = {}, routeId, stopId } = {}) => {
  return fromJS(state.routeSurveyModal.getIn([routeId, stopId, 'photoAlbums', `${getSelectedIndex(state, routeId, stopId)}`, 'albums'], fromJS([]))
  ?.reduce((acc, curr) => {
    const surveyQuestionId = curr.getIn(['photoAlbum', 'surveyQuestion', 'id']);
    const routeId = survey.getIn(['route', 'id']);
    const stopId = survey.getIn(['stop', 'id']);
    const orderId = survey.getIn(['order', 'id']);
    const albumRouteId = curr.getIn(['photoAlbum', 'route', 'id']);
    const albumStopId = curr.getIn(['photoAlbum', 'stop', 'id']);
    const albumOrderId = curr.getIn(['photoAlbum', 'order', 'id']);

    const isOrderAlbum = orderId && albumOrderId && (orderId === albumOrderId);
    const isStopAlbum = !orderId && !albumOrderId && stopId && (
      albumStopId && (stopId === albumStopId)
    );
    const isRouteAlbum = !orderId && !albumOrderId && !stopId && !albumStopId && routeId && (
      albumRouteId && (routeId === albumRouteId)
    );

    return (isOrderAlbum || isStopAlbum || isRouteAlbum) ? (
      { ...acc, [surveyQuestionId]: curr.getIn(['photoAlbum', 'photos']) }
    ) : acc;
  }, {}))
};

export const shouldFetchPhotoAlbums = (state, routeId, stopId) => (
  !state.routeSurveyModal.hasIn([routeId, stopId, 'photoAlbums', `${getSelectedIndex(state, routeId, stopId)}`, 'albums'], false)
  && !state.routeSurveyModal.getIn([routeId, stopId, 'photoAlbums', `${getSelectedIndex(state, routeId, stopId)}`, 'hasFailed'], false)
  && !getIsLoadingPhotoAlbums(state, routeId, stopId)
  && !!getSurveyModalData(state, routeId, stopId).getIn([getSelectedIndex(state, routeId, stopId), 'surveys'])?.size
);

export const getIsLoadingEntityAnswer = (state, { routeId, stopId, surveyQuestion }) => (
  state.routeSurveyModal.getIn([routeId, stopId, 'entityAnswers', `${surveyQuestion.get('uniqueId')}`, 'isLoading'], false)
);

export const getEntityAnswers = (state, { routeId, stopId, surveyQuestion }) => (
  state.routeSurveyModal.getIn([routeId, stopId, 'entityAnswers', `${surveyQuestion.get('uniqueId')}`, 'results'], fromJS([]))
);

export const getIsLoadingSignatureAnswer = (state, { routeId, stopId, surveyAnswerId }) => (
  state.routeSurveyModal.getIn([routeId, stopId, 'signatureAnswers', `${surveyAnswerId}`, 'isLoading'], false)
);

export const getSignatureAnswer = (state, { routeId, stopId, surveyAnswerId }) => (
  state.routeSurveyModal.getIn([routeId, stopId, 'signatureAnswers', `${surveyAnswerId}`], fromJS({}))
);
